import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Rocket,
  Share2,
  Copy,
  ShieldCheck,
  Eye,
  Bell,
  ReceiptText,
  PiggyBank,
  Sparkles,
} from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import ContactDialog from "../components/landing/ContactDialog";
import ShareBar from "../components/landing/ShareBar";
import { toast } from "sonner";

/**
 * /sole-traders — dedicated sole-trader / contractor / small-business campaign
 * landing page. Every CTA funnels into the /enrol/sole-trader iframe wrapper,
 * which secretly loads the ClientOps enrolment form under the NZ Taxation
 * domain.
 */
const ENROL = "/enrol/sole-trader";

export default function SoleTradersPage() {
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}/sole-traders`
      : "https://nztaxation.nz/sole-traders";
  const shareTitle = "Know where your business stands — NZ Taxation Limited";
  const shareDescription =
    "Sole traders, contractors and small business owners: understand your numbers, stay compliant, and keep more of what you earn.";

  const referThisPage = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, text: shareDescription, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied — share it with a fellow business owner");
      }
    } catch {
      /* user cancelled — no-op */
    }
  };

  return (
    <main
      className="min-h-screen bg-[#FAFAFA] text-[#0F172A]"
      data-testid="sole-traders-page"
    >
      <Nav
        onCtaClick={() => (window.location.href = "/#pricing")}
        onContactClick={() => setContactOpen(true)}
      />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div
          aria-hidden
          className="absolute -top-40 -right-40 w-[36rem] h-[36rem] bg-[#C9A349]/10 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-14 md:pb-24">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-[#C9A349] transition-colors"
            data-testid="soletraders-back"
          >
            <ArrowLeft className="w-3 h-3" /> nztaxation.nz
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8 inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-700 bg-white/70 backdrop-blur"
          >
            <Sparkles className="w-3 h-3 text-[#C9A349]" />
            Sole Traders · Contractors · Small Business Owners
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[0.98]"
            data-testid="soletraders-headline"
          >
            Know where your <br />
            business stands <br />
            <span className="text-[#C9A349]">before tax becomes a problem.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-6 text-lg md:text-xl text-slate-600 max-w-2xl leading-relaxed"
          >
            Running a business is hard enough without unexpected tax bills, missed deductions,
            or uncertainty about what you owe. At NZ Taxation we help sole traders and small
            business owners <b>understand their numbers, stay compliant</b>, and keep more of
            what they earn through proactive tax management and clear financial visibility.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Link
              to={ENROL}
              className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:bg-[#C9A349] hover:text-[#0A192F] transition-colors"
              data-testid="soletraders-cta-hero"
              title="NZ Taxation Limited | Monthly Tax Estimates NZ"
            >
              <Rocket className="w-4 h-4" /> Get your free tax health check
            </Link>
            <button
              type="button"
              onClick={() => (window.location.href = "#why-switch")}
              className="inline-flex items-center gap-2 border border-slate-300 px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:border-[#C9A349] hover:text-[#0A192F] transition-colors"
              data-testid="soletraders-secondary-cta"
            >
              Why switch to us <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-500">
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#C9A349]" /> No lock-in contract
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#C9A349]" /> IRD-registered tax agent
            </span>
            <span className="inline-flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-[#C9A349]" /> Fully remote — Auckland based
            </span>
          </div>
        </div>
      </section>

      {/* Why switch */}
      <section id="why-switch" className="py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            Why business owners switch
          </div>
          <h2 className="mt-3 font-heading font-black text-3xl md:text-5xl tracking-tight leading-tight max-w-3xl">
            Most accountants file returns. <br />
            <span className="text-[#C9A349]">We help you understand your business.</span>
          </h2>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            <BenefitCard
              icon={ReceiptText}
              title="Review before GST submission"
              body="Before any GST return is filed, you get a copy to review. Catch missed business expenses, correct inaccuracies, confirm income is recorded correctly, and avoid preventable errors."
            />
            <BenefitCard
              icon={PiggyBank}
              title="Legally maximise your tax savings"
              body="We make sure you're claiming every business deduction you're entitled to — so you're not paying tax you shouldn't be paying. Keep more of what you earn, plan ahead with confidence."
            />
            <BenefitCard
              icon={ShieldCheck}
              title="Stay IRD review & audit ready"
              body="Good records create confidence. We keep your financial records organised and compliant, so if IRD ever has questions, you're not scrambling."
            />
            <BenefitCard
              icon={Eye}
              title="Real-time access through your portal"
              body="Your finances shouldn't be a mystery. Our secure client portal gives you live visibility of financial records, GST information, documents, communications and your current tax position."
            />
            <BenefitCard
              icon={Bell}
              title="Ongoing tax reminders"
              body="Most tax problems start with poor visibility. Reminders and updates keep you ahead of GST dates, tax payments, filing deadlines and upcoming obligations — no surprises, no last-minute panic."
              wide
            />
          </div>
        </div>
      </section>

      {/* Who this is for */}
      <section className="py-20 md:py-28 bg-[#0A192F] text-white relative overflow-hidden">
        <div
          aria-hidden
          className="absolute top-1/2 -left-40 w-[36rem] h-[36rem] bg-[#C9A349]/10 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#C9A349]">
            Who this is for
          </div>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight leading-tight max-w-3xl">
            For hardworking New Zealand <br />
            <span className="text-[#C9A349]">business owners.</span>
          </h2>
          <p className="mt-5 text-slate-300 text-lg max-w-2xl leading-relaxed">
            Whether you&apos;re a tradie, contractor, Uber or delivery driver, cleaner,
            caregiver, consultant, freelancer or small business owner — you deserve to know:
          </p>

          <ul className="mt-8 grid md:grid-cols-2 gap-3 text-slate-100">
            {[
              "How much you're really making",
              "Where your money is going",
              "What tax is coming",
              "Whether you're claiming everything you're entitled to",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 border-l-2 border-[#C9A349] pl-4 py-1">
                <CheckCircle2 className="w-5 h-5 text-[#C9A349] flex-shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10 border-l-4 border-[#C9A349] pl-5 text-slate-200 max-w-2xl">
            <p className="font-heading font-black text-xl md:text-2xl leading-snug">
              Financial clarity creates better decisions.<br />
              Better decisions create better businesses.
            </p>
          </div>
        </div>
      </section>

      {/* Referral block */}
      <section className="py-20 md:py-28 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">
            Community referral
          </div>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight leading-tight">
            Help another business owner <br />
            <span className="text-[#C9A349]">avoid tax stress.</span>
          </h2>
          <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed">
            If NZ Taxation has helped you feel more confident about your business finances,
            we&apos;d appreciate you sharing this with friends, family, colleagues and fellow
            business owners. A simple introduction could help someone gain clarity,
            confidence and control over their financial future.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={referThisPage}
              className="inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-6 py-4 font-heading font-black uppercase tracking-[0.14em] text-sm hover:bg-[#0A192F] hover:text-white transition-colors"
              data-testid="soletraders-refer-btn"
            >
              <Share2 className="w-4 h-4" /> Refer a sole trader today
            </button>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(url);
                  toast.success("Link copied to clipboard");
                } catch {
                  toast.error("Couldn't copy — please copy the URL manually");
                }
              }}
              className="inline-flex items-center gap-2 border border-slate-300 px-6 py-4 font-heading font-bold uppercase tracking-[0.14em] text-sm hover:border-[#C9A349] transition-colors"
              data-testid="soletraders-copy-link"
            >
              <Copy className="w-4 h-4" /> Copy page link
            </button>
          </div>
        </div>
      </section>

      {/* Final CTA — Free health check */}
      <section className="bg-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#C9A349]">
            No cost · No obligation
          </div>
          <h2 className="mt-4 font-heading font-black text-4xl md:text-6xl tracking-tight leading-[1.02]">
            Free sole trader <br />
            <span className="text-[#C9A349]">tax health check.</span>
          </h2>
          <p className="mt-6 text-slate-600 max-w-2xl mx-auto text-lg">
            In one conversation we&apos;ll surface what most sole traders never see:
          </p>

          <ul className="mt-8 max-w-lg mx-auto space-y-3 text-left">
            {[
              "Potential missed deductions",
              "Areas to improve bookkeeping",
              "Opportunities to improve tax planning",
              "Upcoming tax obligations",
              "Better visibility over your business finances",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3 border-l-2 border-[#C9A349] bg-white px-4 py-3">
                <CheckCircle2 className="w-5 h-5 text-[#C9A349] flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{line}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10">
            <Link
              to={ENROL}
              className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-8 py-5 font-heading font-black uppercase tracking-[0.14em] text-base hover:bg-[#C9A349] hover:text-[#0A192F] transition-colors"
              data-testid="soletraders-cta-final"
              title="NZ Taxation Limited | Monthly Tax Estimates NZ"
            >
              Get started with NZ Taxation <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Core marketing message */}
      <section className="bg-[#0A192F] text-white bg-grid-dark">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-24 text-center">
          <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-[#C9A349]">
            Our promise
          </div>
          <p className="mt-6 font-heading font-black text-2xl md:text-4xl leading-[1.15] tracking-tight">
            We don&apos;t just help you file taxes. <br />
            We help you <span className="text-[#C9A349]">understand your business</span>, stay
            ahead of your obligations, and make informed financial decisions with confidence.
          </p>
          <p className="mt-8 text-slate-300 text-lg italic max-w-2xl mx-auto">
            Your business deserves more than compliance. It deserves clarity.
          </p>
        </div>
      </section>

      <ShareBar url={url} title={shareTitle} description={shareDescription} />
      <Footer />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </main>
  );
}

function BenefitCard({ icon: Icon, title, body, wide }) {
  return (
    <div
      className={`border border-slate-200 bg-white p-7 md:p-8 flex gap-5 items-start hover:-translate-y-1 hover:border-[#C9A349] transition-all ${
        wide ? "md:col-span-2" : ""
      }`}
    >
      <div className="w-11 h-11 flex items-center justify-center bg-[#0A192F] text-[#C9A349] flex-shrink-0">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h3 className="font-heading font-black text-lg md:text-xl text-[#0A192F] tracking-tight">
          {title}
        </h3>
        <p className="mt-2 text-slate-600 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}
